import toast from "react-hot-toast";
import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";

export type ApiErrorResponse = {
  trigger: string;
  message: string;
};

export type ApiResponse<T> = {
  res: T | null;
  error: AxiosError | null;
};

export async function handleApi<T>(
  promise: Promise<T>,
  showToast: boolean = true
): Promise<ApiResponse<T>> {
  try {
    const res = await promise;
    return { res, error: null };
  } catch (error) {
    const axiosError = error as AxiosError;

    if (axiosError.code === "ERR_CANCELED") {
      console.warn("Request canceled");
      return { res: null, error: null };
    }

    logErrorToSentry(axiosError);

    if (showToast) {
      const message = getErrorMessage(axiosError);
      toast.error(message);
    }

    return { res: null, error: axiosError };
  }
}

function logErrorToSentry(error: AxiosError) {
  const data = error.response?.data;
  const hasTrigger = data && typeof data === "object" && "trigger" in data;

  if (hasTrigger) return;

  const status = error.response?.status;
  const code = error.code;

  const ignoreErrors = [404, 401, 403];
  if (
    ignoreErrors.includes(status!) ||
    code === "ECONNABORTED" ||
    code === "ERR_NETWORK"
  ) {
    return;
  }

  const endpoint = error.config?.url || "unknown-endpoint";
  const method = error.config?.method?.toUpperCase() || "GET";
  const statusNum = typeof status === "number" ? status : 0;

  const sentryError = new Error(
    `API Error: ${method} ${endpoint} (${status || "no-response"})`
  );
  (sentryError as any).originalError = error;

  Sentry.captureException(sentryError, {
    tags: {
      http_status: status,
      http_method: method,
      endpoint,
      error_type: "uncontrolled_api_error",
    },
    contexts: {
      request: {
        url: error.config?.url,
        method,
        headers: error.config?.headers,
        params: error.config?.params,
      },
      response: {
        status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      },
      original_error: {
        name: error.name,
        message: error.message,
        code: error.code,
      },
    },
    fingerprint: [method, endpoint, String(status)],
    level: statusNum >= 500 ? "error" : "warning",
  });

  console.error("Uncontrolled API Error:", method, endpoint, status);
}

export function getErrorMessage(error: AxiosError): string {
  const defaultMessage = "An unexpected error has occurred";
  const data = error.response?.data;

  if (typeof data === "object" && data !== null) {
    const { message, trigger } = data as ApiErrorResponse;
    return message || trigger || defaultMessage;
  }

  if (typeof data === "string") return data;
  return error.message || defaultMessage;
}
